import moment from "moment-timezone";

/** Application-wide display format for local timezone timestamps (12-hour). */
export const DISPLAY_DATETIME_FORMAT = "DD MMM YYYY, hh:mm:ss A";

/** Compact lists/cards — same as full format (includes seconds). */
export const DISPLAY_DATETIME_SHORT_FORMAT = DISPLAY_DATETIME_FORMAT;

const MIN_VALID_MS = new Date("2020-01-01").getTime();

function isPresent(value) {
  return value !== undefined && value !== null && value !== "";
}

/** Browser IANA timezone sent on every API request and socket handshake. */
export const CLIENT_TIMEZONE_HEADER = "X-Client-Timezone";

/** Browser IANA timezone (e.g. Asia/Kolkata). */
export function getClientTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function hasTimezoneInfo(value) {
  return /[zZ]$/.test(value) || /[+-]\d{2}:?\d{2}$/.test(value);
}

/** Normalize Postgres / API strings to ISO-like form before UTC parse. */
function normalizeTimestampString(value) {
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(trimmed)) {
    return trimmed.replace(" ", "T");
  }
  return trimmed;
}

/**
 * Parse API/DB UTC instants. Storage contract: naive strings are UTC.
 * Returns a moment in UTC mode, or null when invalid.
 */
export function parseUtcInstant(value) {
  if (!isPresent(value)) return null;

  if (typeof value === "number") {
    if (value === 0) return null;
    const m =
      value >= MIN_VALID_MS
        ? moment.utc(value)
        : value > 1e9 && value < 1e12
          ? moment.unix(value).utc()
          : null;
    return m?.isValid() && m.valueOf() >= MIN_VALID_MS ? m : null;
  }

  if (typeof value === "string") {
    const trimmed = normalizeTimestampString(value);
    if (!trimmed) return null;

    if (/^\d+$/.test(trimmed)) {
      const n = Number(trimmed);
      if (n >= MIN_VALID_MS) {
        const m = moment.utc(n);
        return m.isValid() ? m : null;
      }
      if (n > 1e9 && n < 1e12) {
        const m = moment.unix(n).utc();
        return m.isValid() ? m : null;
      }
      return null;
    }

    if (hasTimezoneInfo(trimmed)) {
      const m = moment.parseZone(trimmed);
      return m.isValid() && m.valueOf() >= MIN_VALID_MS ? m.utc() : null;
    }

    const m = moment.utc(trimmed);
    return m.isValid() && m.valueOf() >= MIN_VALID_MS ? m : null;
  }

  if (value instanceof Date) {
    const m = moment.utc(value);
    return m.isValid() && m.valueOf() >= MIN_VALID_MS ? m : null;
  }

  return null;
}

/** UTC instant as epoch ms, or null. */
export function parseToLocalMs(value) {
  return parseUtcInstant(value)?.valueOf() ?? null;
}

/**
 * Display a UTC instant in the user's browser/system timezone.
 * Example: 2026-06-03T09:30:00Z → "03 Jun 2026, 03:00:00 PM" (IST)
 */
export function formatLocalDateTime(value, { fallback = "—" } = {}) {
  const m = parseUtcInstant(value);
  if (!m) return fallback;
  return m.local().format(DISPLAY_DATETIME_FORMAT);
}

/** Date + 12-hour time for compact UI (equipment status, etc.). */
export function formatLocalDateTimeShort(value, { fallback = "—" } = {}) {
  const m = parseUtcInstant(value);
  if (!m) return fallback;
  return m.local().format(DISPLAY_DATETIME_SHORT_FORMAT);
}

/** Hour label for charts in local timezone. */
export function formatLocalHour(value) {
  const m = parseUtcInstant(value);
  if (!m) return "—";
  return m.local().format("hh:mm A");
}

/** Time only in local timezone (12-hour). */
export function formatLocalTime(value, { fallback = "—" } = {}) {
  const m = parseUtcInstant(value);
  if (!m) return fallback;
  return m.local().format("hh:mm A");
}

/** Date only in local timezone. */
export function formatLocalDate(value, { fallback = "—" } = {}) {
  const m = parseUtcInstant(value);
  if (!m) return fallback;
  return m.local().format("DD MMM YYYY");
}

/**
 * Locations module: Last Updated / WL Last Updated / Rain Last Updated.
 * Local browser timezone, 12-hour time.
 */
export function formatLocationLastUpdated(value, { fallback = "—" } = {}) {
  const m = parseUtcInstant(value);
  if (!m) return fallback;
  return m.local().format(DISPLAY_DATETIME_SHORT_FORMAT);
}

/** Format a calendar Date in the client timezone for API date params (YYYY-MM-DD). */
export function formatCalendarDateForApi(date) {
  if (!date) return undefined;
  return moment.tz(date, getClientTimezone()).format("YYYY-MM-DD");
}
